import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Building2, Users, Mail, Search, Filter, X, ChevronDown, ChevronRight,
  ArrowLeft, Copy, Check, ShieldCheck, RefreshCw, Trash2, AlertTriangle, KeyRound,
  Send, Eye, Edit3, FileText, MailCheck, Plus, Save, Pencil,
} from "lucide-react";
import SEO from "../components/SEO";
import { useAuth } from "../components/AuthContext";
import { getIdTypeMeta, getUserBadge } from "../lib/userIdTypes";
import { API } from "../apiBase";
const ROLE_LABEL = {
  titulaire: "Titulaire",
  consultant: "Consultant",
  modificateur: "Modificateur",
  gestionnaire: "Gestionnaire",
};
const ROLE_COLOR = {
  titulaire: "bg-[rgba(110,231,197,0.14)] border-[rgba(110,231,197,0.45)] text-[#047857]",
  consultant: "bg-[rgba(127,179,255,0.10)] border-[rgba(127,179,255,0.30)] text-[#0284C7]",
  modificateur: "bg-[rgba(168,140,255,0.12)] border-[rgba(168,140,255,0.40)] text-[#7C3AED]",
  gestionnaire: "bg-[rgba(255,170,80,0.12)] border-[rgba(255,170,80,0.40)] text-[#ffce99]",
};

const STATUS_LABEL = { pending: "En attente", active: "Actif", cancelled: "Résilié" };
const STATUS_COLOR = {
  pending: "bg-[rgba(255,170,80,0.10)] border-[rgba(255,170,80,0.36)] text-[#ffce99]",
  active: "bg-[rgba(110,231,197,0.12)] border-[rgba(110,231,197,0.45)] text-[#047857]",
  cancelled: "bg-[rgba(255,120,120,0.10)] border-[rgba(255,120,120,0.36)] text-[#DC2626]",
};

// Admin workflow — Maincare creation tracking
const WORKFLOW_LABEL = {
  received: "Reçue",
  maincare_in_progress: "En cours sur Maincare",
  maincare_created: "Créée sur Maincare",
  activated: "Activée",
};
const WORKFLOW_COLOR = {
  received: "bg-[rgba(127,179,255,0.10)] border-[rgba(127,179,255,0.32)] text-[#0284C7]",
  maincare_in_progress: "bg-[rgba(255,170,80,0.10)] border-[rgba(255,170,80,0.40)] text-[#ffce99]",
  maincare_created: "bg-[rgba(168,140,255,0.10)] border-[rgba(168,140,255,0.40)] text-[#7C3AED]",
  activated: "bg-[rgba(110,231,197,0.12)] border-[rgba(110,231,197,0.45)] text-[#047857]",
};
const WORKFLOW_OPTIONS = ["received", "maincare_in_progress", "maincare_created", "activated"];

// Payment — read-only (synchronised from Stripe webhooks)
const PAYMENT_LABEL = {
  ohmyvue: "Inclus OhMyVue",
  pending_payment: "Paiement en attente",
  active: "Payé",
  past_due: "Impayé",
  cancelled: "Annulé",
  legacy: "Hors Stripe",
};
const PAYMENT_COLOR = {
  pending_payment: "bg-[rgba(255,170,80,0.10)] border-[rgba(255,170,80,0.36)] text-[#ffce99]",
  active: "bg-[rgba(110,231,197,0.12)] border-[rgba(110,231,197,0.45)] text-[#047857]",
  past_due: "bg-[rgba(255,120,120,0.10)] border-[rgba(255,120,120,0.36)] text-[#DC2626]",
  cancelled: "bg-[rgba(127,127,127,0.10)] border-[rgba(127,127,127,0.36)] text-[#64748B]",
  legacy: "bg-[rgba(127,179,255,0.08)] border-[rgba(127,179,255,0.28)] text-[var(--mc-muted)]",
};

export default function AdminMssante() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("structures");
  const [structures, setStructures] = useState([]);
  const [proUsers, setProUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [openStructureId, setOpenStructureId] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setErr("");
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/admin/mssante/structures`),
        axios.get(`${API}/admin/pro-users`),
      ]);
      setStructures(s.data || []);
      setProUsers(p.data || []);
    } catch (e) {
      setErr("Impossible de charger les données. Êtes-vous bien connecté en admin ?");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  const refresh = () => load({ silent: true });
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return structures;
    const q = query.toLowerCase().trim();
    return structures.filter((s) =>
      (s.structure_name || "").toLowerCase().includes(q) ||
      (s.structure_id || "").includes(q) ||
      (s.users || []).some((u) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.professional_id || "").includes(q)
      ) ||
      (s.mailboxes || []).some((m) => (m.full_email || "").toLowerCase().includes(q))
    );
  }, [structures, query]);

  const allUsers = useMemo(() => {
    const out = [];
    for (const s of structures) {
      for (const u of (s.users || [])) {
        out.push({ ...u, structure_name: s.structure_name, structure_id: s.structure_id, structure_id_type: s.structure_id_type });
      }
    }
    return out;
  }, [structures]);

  const allMailboxes = useMemo(() => {
    const out = [];
    for (const s of structures) {
      for (const m of (s.mailboxes || [])) {
        out.push({ ...m, structure_name: s.structure_name, structure_id: s.structure_id, structure_id_type: s.structure_id_type });
      }
    }
    return out;
  }, [structures]);

  const pendingChangesCount = useMemo(
    () => allMailboxes.filter((m) => m.has_pending_admin_changes).length,
    [allMailboxes]
  );

  const filteredUsers = useMemo(() => {
    if (!query.trim()) return allUsers;
    const q = query.toLowerCase().trim();
    return allUsers.filter((u) =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.professional_id || "").includes(q) ||
      (u.structure_name || "").toLowerCase().includes(q)
    );
  }, [allUsers, query]);

  const filteredMailboxes = useMemo(() => {
    if (!query.trim()) return allMailboxes;
    const q = query.toLowerCase().trim();
    return allMailboxes.filter((m) =>
      (m.full_email || "").toLowerCase().includes(q) ||
      (m.structure_name || "").toLowerCase().includes(q) ||
      (m.responsible || "").toLowerCase().includes(q) ||
      (m.billing_ref || "").toLowerCase().includes(q)
    );
  }, [allMailboxes, query]);

  return (
    <div className="min-h-screen bg-[var(--mc-navy-950)] hex-grid pt-28 pb-16 px-5 lg:px-8" data-testid="page-admin-mssante">
      <SEO title="Admin MSSanté — Super-admin" canonical="/admin/mssante" noIndex />

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-7">
          <div className="min-w-0">
            <Link to="/admin" className="text-[12px] text-[var(--mc-muted)] hover:text-[#0F2B5B] inline-flex items-center gap-1.5">
              <ArrowLeft size={12} /> Retour admin
            </Link>
            <div className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#047857]">
              <ShieldCheck size={13} /> Super-admin · Maincare bridge
            </div>
            <h1 className="font-display text-[#0F2B5B] text-3xl lg:text-4xl mt-2">Gestion MSSanté</h1>
            <p className="mt-1 text-[13px] text-[var(--mc-muted)]">
              Toutes les commandes consolidées par structure — données prêtes à recopier dans Maincare.
              Connecté : <span className="text-[#0F2B5B]">{user?.email}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <RppsImportButton />
            <button onClick={load} data-testid="admin-mss-refresh"
                    className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-2">
              <RefreshCw size={13} /> Actualiser
            </button>
            <button onClick={() => navigate("/admin")} className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium">
              Articles blog
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 lg:gap-4 mb-7">
          <Stat icon={Building2} label="Structures" value={structures.length} />
          <Stat icon={Users} label="Utilisateurs" value={allUsers.length} />
          <Stat icon={Mail} label="Boîtes aux lettres" value={allMailboxes.length} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="admin-mss-tabs">
          <TabBtn active={tab === "structures"} onClick={() => setTab("structures")} icon={Building2} label="Structures" count={structures.length} testid="tab-structures" />
          <TabBtn active={tab === "users"} onClick={() => setTab("users")} icon={Users} label="Utilisateurs" count={allUsers.length} testid="tab-users" />
          <TabBtn active={tab === "mailboxes"} onClick={() => setTab("mailboxes")} icon={Mail} label="Boîtes aux lettres" count={allMailboxes.length} testid="tab-mailboxes" />
          <TabBtn active={tab === "pro-accounts"} onClick={() => setTab("pro-accounts")} icon={ShieldCheck} label="Comptes pro" count={proUsers.length} testid="tab-pro-accounts" />
        </div>

        {/* Search */}
        <div className="relative mb-5 max-w-md">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--mc-muted)]" />
          <input data-testid="admin-mss-search"
                 className="mc-input w-full pl-9 pr-9"
                 placeholder="Rechercher (nom, SIRET, email, RPPS...)"
                 value={query} onChange={(e) => setQuery(e.target.value)} />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--mc-muted)] hover:text-[#0F2B5B]">
              <X size={13} />
            </button>
          )}
        </div>

        {loading && <div className="text-[var(--mc-muted)]">Chargement…</div>}
        {err && <div className="text-[#DC2626] text-[13px]">{err}</div>}

        {!loading && !err && pendingChangesCount > 0 && (
          <div
            className="mb-5 flex items-center gap-3 rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.06)] px-4 py-3"
            data-testid="admin-mss-pending-banner"
          >
            <AlertTriangle size={16} className="text-[#DC2626] shrink-0" />
            <div className="flex-1 text-[13px]">
              <span className="text-[#DC2626] font-medium">{pendingChangesCount} BAL</span>
              <span className="text-[var(--mc-muted)]">
                {" "}avec des modifications en attente d'acquittement côté professionnel.
              </span>
            </div>
          </div>
        )}

        {!loading && !err && (
          <>
            {tab === "structures" && (
              <StructuresList items={filtered} openId={openStructureId} setOpenId={setOpenStructureId} onChanged={refresh} />
            )}
            {tab === "users" && <UsersTable items={filteredUsers} />}
            {tab === "mailboxes" && <MailboxesTable items={filteredMailboxes} onChanged={refresh} />}
            {tab === "pro-accounts" && (
              <ProAccountsTable items={proUsers} query={query} onChanged={refresh} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =================== sub-components ===================

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="mc-card p-4 lg:p-5 flex items-center gap-3">
      <div className="grid place-items-center h-10 w-10 rounded-full bg-[rgba(45,123,255,0.14)] border border-[rgba(45,123,255,0.32)] text-[#0284C7]">
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--mc-muted)]">{label}</div>
        <div className="font-display text-[#0F2B5B] text-2xl">{value}</div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-2 border transition ${
              active
                ? "bg-[var(--mc-blue-500)] border-[var(--mc-blue-500)] text-white"
                : "bg-transparent border-[rgba(127,179,255,0.18)] text-[var(--mc-muted)] hover:text-[#0F2B5B] hover:border-[rgba(127,179,255,0.32)]"
            }`}>
      <Icon size={13} /> {label}
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-[rgba(255,255,255,0.18)]" : "bg-[rgba(127,179,255,0.10)]"}`}>{count}</span>
    </button>
  );
}

function StructuresList({ items, openId, setOpenId, onChanged }) {
  const [manageSid, setManageSid] = useState(null);
  if (items.length === 0) {
    return <Empty icon={Building2} text="Aucune structure pour le moment." />;
  }
  return (
    <div className="grid gap-3" data-testid="admin-mss-structures">
      {items.map((s) => {
        const k = `${s.structure_id_type}:${s.structure_id}`;
        const open = openId === k;
        return (
          <article key={k} className="mc-card overflow-hidden" data-testid={`structure-${s.structure_id}`}>
            <button onClick={() => setOpenId(open ? null : k)}
                    className="w-full flex items-center gap-4 p-5 text-left hover:bg-[rgba(45,123,255,0.04)] transition">
              <div className="grid place-items-center h-11 w-11 rounded-full bg-[rgba(45,123,255,0.14)] border border-[rgba(45,123,255,0.30)] text-[#0284C7] shrink-0">
                <Building2 size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[#0F2B5B] font-medium truncate">{s.structure_name}</div>
                <div className="mt-0.5 flex items-center gap-3 text-[12px] text-[var(--mc-muted)] flex-wrap">
                  <span>{s.structure_id_type} <span className="text-[#475569] font-mono">{s.structure_id}</span></span>
                  <span>·</span>
                  <span>{s.user_count} utilisateur{s.user_count > 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span>{s.mailbox_count} boîte{s.mailbox_count > 1 ? "s" : ""}</span>
                </div>
              </div>
              <div className="text-[var(--mc-muted)]">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</div>
            </button>

            {open && (
              <div className="border-t border-[rgba(127,179,255,0.10)] p-5 grid gap-5 bg-[rgba(0,0,0,0.18)]">
                {/* Mailboxes */}
                <section>
                  <h4 className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7] flex items-center gap-2 mb-2">
                    <Mail size={12} /> Boîtes aux lettres
                  </h4>
                  <div className="grid gap-2">
                    {s.mailboxes.map((m) => (
                      <MailboxRow key={m.id} m={m} onChanged={onChanged} />
                    ))}
                  </div>
                </section>
                {/* Users */}
                <section>
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <h4 className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7] flex items-center gap-2">
                      <Users size={12} /> Utilisateurs
                    </h4>
                    <button
                      onClick={() => setManageSid(s.structure_ref_id)}
                      data-testid={`structure-${s.structure_id}-manage`}
                      className="px-3 py-1 rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 border border-[rgba(110,231,197,0.45)] text-[#047857] hover:bg-[rgba(110,231,197,0.10)]"
                      title="Ajouter/modifier/supprimer des utilisateurs et gérer les accès BAL"
                    >
                      <Users size={11} /> Gérer utilisateurs & accès
                    </button>
                  </div>
                  <div className="grid gap-2">
                    {s.users.map((u) => <UserRow key={u.id || u.email || u.professional_id} u={u} />)}
                  </div>
                </section>
              </div>
            )}
          </article>
        );
      })}
      {manageSid && (
        <ManageStructureModal
          sid={manageSid}
          onClose={() => setManageSid(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

function CopyChip({ value, label }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button type="button"
            onClick={async (e) => {
              e.stopPropagation();
              try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch (err) { console.warn("clipboard write failed", err); }
            }}
            className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-md border border-[rgba(127,179,255,0.20)] text-[var(--mc-muted)] hover:text-[#0F2B5B] hover:border-[rgba(127,179,255,0.40)] transition"
            title={`Copier ${label || value}`}>
      {done ? <Check size={11} className="text-[#047857]" /> : <Copy size={11} />}
      <span className="font-mono truncate max-w-[200px]">{value}</span>
    </button>
  );
}

function RppsImportButton() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/rpps/status`);
      setState(data);
      return data;
    } catch (e) {
      return null;
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStatus(); }, []);

  // Poll while a job is running
  useEffect(() => {
    if (!state || (state.status !== "downloading" && state.status !== "importing")) return;
    const t = setInterval(fetchStatus, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.status]);

  const trigger = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/rpps/refresh`);
      await fetchStatus();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Lancement impossible.");
    } finally {
      setBusy(false);
    }
  };

  const count = state?.directory_count || 0;
  const running = state?.status === "downloading" || state?.status === "importing";
  const label = running ? state?.message || "Import en cours…" :
                count > 0 ? `Annuaire RPPS · ${count.toLocaleString("fr-FR")}` :
                "Importer l'annuaire RPPS";

  return (
    <button
      onClick={trigger}
      disabled={busy || running}
      data-testid="admin-rpps-import"
      title={state?.last_imported_at ? `Dernier import : ${new Date(state.last_imported_at).toLocaleString("fr-FR")}` : "Télécharger et importer l'annuaire ANS"}
      className="btn-ghost px-3 py-2 rounded-full text-[11.5px] font-medium inline-flex items-center gap-2 disabled:opacity-70"
    >
      <RefreshCw size={12} className={running ? "animate-spin" : ""} /> {label}
    </button>
  );
}


function ManageStructureModal({ sid, onClose, onChanged }) {
  const [users, setUsers] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingAccessFor, setSavingAccessFor] = useState(null);

  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch users + the structure card (with mailboxes) in parallel
      const [usersResp, structResp] = await Promise.all([
        axios.get(`${API}/admin/structures/${sid}/users`),
        axios.get(`${API}/admin/mssante/structures`),
      ]);
      setUsers(usersResp.data || []);
      const s = (structResp.data || []).find((x) => x.structure_ref_id === sid);
      setMailboxes(s?.mailboxes || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [sid]);

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditForm({ ...u });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(null); };

  const saveEdit = async () => {
    if (!editForm) return;
    setError(""); setInfo("");
    try {
      const payload = {
        first_name: editForm.first_name, last_name: editForm.last_name,
        email: editForm.email, phone: editForm.phone || null,
        profession: editForm.profession, id_type: editForm.id_type,
        professional_id: editForm.professional_id,
      };
      await axios.put(`${API}/admin/structures/${sid}/users/${editForm.id}`, payload);
      setInfo("Utilisateur mis à jour, email d'information envoyé au responsable.");
      cancelEdit();
      await reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Mise à jour impossible.");
    }
  };

  const deleteUser = async (u) => {
    const full = `${u.first_name} ${u.last_name}`.trim();
    if (!window.confirm(`Supprimer définitivement ${full} de la structure ?\nLe responsable sera notifié par email.`)) return;
    setError(""); setInfo("");
    try {
      await axios.delete(`${API}/admin/structures/${sid}/users/${u.id}`);
      setInfo(`Utilisateur ${full} supprimé.`);
      await reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const saveAccess = async (mbId, cotitulaireIds, delegataireIds) => {
    setSavingAccessFor(mbId);
    setError(""); setInfo("");
    try {
      await axios.put(`${API}/admin/mssante-orders/${mbId}/access`, {
        cotitulaire_user_ids: cotitulaireIds,
        delegataire_user_ids: delegataireIds,
      });
      setInfo("Accès BAL mis à jour, email d'information envoyé au responsable.");
      await reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Mise à jour impossible.");
    } finally {
      setSavingAccessFor(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid={`manage-structure-modal-${sid}`}
    >
      <div
        className="w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-2xl border border-[rgba(127,179,255,0.30)] bg-[#0A1A33] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[rgba(127,179,255,0.18)]">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-[#047857]" />
            <div className="text-[13.5px] text-[#0F2B5B]">Gestion utilisateurs &amp; accès BAL</div>
          </div>
          <button onClick={onClose} data-testid="manage-modal-close"
                  className="text-[var(--mc-muted)] hover:text-[#0F2B5B] p-1.5 rounded-md hover:bg-[#F1F5F9]">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 grid gap-5">
          {loading && <div className="text-[13px] text-[var(--mc-muted)]">Chargement…</div>}
          {info && (
            <div className="rounded-lg border border-[rgba(110,231,197,0.40)] bg-[rgba(110,231,197,0.07)] p-2.5 text-[12.5px] text-[#047857] inline-flex items-center gap-2">
              <Check size={12} /> {info}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.06)] p-2.5 text-[12.5px] text-[#DC2626]">
              {error}
            </div>
          )}

          {!loading && (
            <>
              {/* Users table */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7]">
                    Utilisateurs de la structure ({users.length})
                  </h4>
                  <button onClick={() => setShowAdd((v) => !v)}
                          data-testid="manage-add-user-toggle"
                          className="px-3 py-1 rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 border border-[rgba(110,231,197,0.45)] text-[#047857] hover:bg-[rgba(110,231,197,0.10)]">
                    {showAdd ? <X size={11} /> : <Plus size={11} />} {showAdd ? "Annuler" : "Ajouter un utilisateur"}
                  </button>
                </div>
                {showAdd && (
                  <AddUserForm sid={sid}
                               onAdded={async () => { setShowAdd(false); setInfo("Utilisateur ajouté, email d'information envoyé au responsable."); await reload(); onChanged && onChanged(); }}
                               onError={(msg) => setError(msg)} />
                )}
                <div className="grid gap-1.5 mt-2">
                  {users.map((u) => (
                    <div key={u.id}
                         className="rounded-lg border border-[rgba(127,179,255,0.16)] bg-[rgba(45,123,255,0.04)] p-2.5"
                         data-testid={`manage-user-${u.id}`}>
                      {editingId === u.id ? (
                        <div className="grid sm:grid-cols-2 gap-2">
                          <input className="mc-input text-[12px]" placeholder="Prénom"
                                 value={editForm.first_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} />
                          <input className="mc-input text-[12px]" placeholder="Nom"
                                 value={editForm.last_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))} />
                          <input className="mc-input text-[12px]" placeholder="Email"
                                 value={editForm.email || ""} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
                          <input className="mc-input text-[12px]" placeholder="Téléphone"
                                 value={editForm.phone || ""} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
                          <input className="mc-input text-[12px]" placeholder="Profession"
                                 value={editForm.profession || ""} onChange={(e) => setEditForm((f) => ({ ...f, profession: e.target.value }))} />
                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <select className="mc-input text-[12px]" value={editForm.id_type || "RPPS"}
                                    onChange={(e) => setEditForm((f) => ({ ...f, id_type: e.target.value }))}>
                              <option>RPPS</option><option>ADELI</option>
                            </select>
                            <input className="mc-input text-[12px]" placeholder="N° pro"
                                   value={editForm.professional_id || ""} onChange={(e) => setEditForm((f) => ({ ...f, professional_id: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2 flex justify-end gap-2 pt-1">
                            <button onClick={cancelEdit} className="btn-ghost px-3 py-1 rounded-full text-[11.5px]">Annuler</button>
                            <button onClick={saveEdit} data-testid={`manage-user-${u.id}-save`}
                                    className="btn-primary px-3 py-1 rounded-full text-[11.5px] inline-flex items-center gap-1.5">
                              <Save size={11} /> Enregistrer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="text-[13.5px] text-[#0F2B5B]">
                              <strong>{u.first_name} {u.last_name}</strong>
                              <span className="text-[var(--mc-muted)] text-[11.5px]"> — {u.profession}</span>
                            </div>
                            <div className="text-[11.5px] text-[var(--mc-muted)] flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                              <span className="font-mono">{u.email}</span>
                              {u.phone && <span>· {u.phone}</span>}
                              <span>· {u.id_type} <span className="font-mono">{u.professional_id}</span></span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => startEdit(u)} data-testid={`manage-user-${u.id}-edit`}
                                    className="px-2.5 py-1 rounded-full text-[11px] border border-[rgba(127,179,255,0.30)] text-[#0284C7] hover:bg-[rgba(45,123,255,0.10)] inline-flex items-center gap-1">
                              <Pencil size={11} /> Modifier
                            </button>
                            <button onClick={() => deleteUser(u)} data-testid={`manage-user-${u.id}-delete`}
                                    className="px-2.5 py-1 rounded-full text-[11px] border border-[rgba(255,120,120,0.35)] text-[#DC2626] hover:bg-[rgba(255,120,120,0.08)] inline-flex items-center gap-1">
                              <Trash2 size={11} /> Suppr.
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* BAL access section */}
              <section>
                <h4 className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7] mb-2 flex items-center gap-2">
                  <Mail size={12} /> Accès aux boîtes ({mailboxes.length})
                </h4>
                <div className="grid gap-3">
                  {mailboxes.map((m) => (
                    <BalAccessEditor key={m.id} mailbox={m} users={users}
                                     onSave={(co, de) => saveAccess(m.id, co, de)}
                                     saving={savingAccessFor === m.id} />
                  ))}
                </div>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[rgba(127,179,255,0.18)]">
          <button onClick={onClose} className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium">Fermer</button>
        </div>
      </div>
    </div>
  );
}

function AddUserForm({ sid, onAdded, onError }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    profession: "Opticien-lunetier", id_type: "RPPS", professional_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [lookupState, setLookupState] = useState({ status: "idle", message: "" });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Auto-lookup the pro in the ANS RPPS directory when the ID has 9 or 11 digits.
  useEffect(() => {
    const digits = (form.professional_id || "").replace(/\D/g, "");
    if (digits.length !== 9 && digits.length !== 11 && digits.length !== 12 && digits.length !== 13) {
      setLookupState({ status: "idle", message: "" });
      return;
    }
    let alive = true;
    setLookupState({ status: "loading", message: "Recherche dans l'annuaire ANS…" });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/rpps/lookup`, { params: { id: digits } });
        if (!alive) return;
        setForm((f) => ({
          ...f,
          first_name: f.first_name || data.first_name || "",
          last_name: f.last_name || data.last_name || "",
          profession: data.profession_label || f.profession,
          id_type: data.id_type || f.id_type,
        }));
        setLookupState({
          status: "found",
          message: `Trouvé : ${data.civility_label || ""} ${data.first_name} ${data.last_name} — ${data.profession_label}${data.specialty_label ? " (" + data.specialty_label + ")" : ""}`,
        });
      } catch (e) {
        if (!alive) return;
        setLookupState({
          status: "not_found",
          message: e?.response?.status === 404
            ? "Numéro introuvable dans l'annuaire ANS — saisie manuelle requise."
            : (e?.response?.data?.detail || "Vérification impossible."),
        });
      }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [form.professional_id]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/structures/${sid}/users`, {
        ...form, phone: form.phone || null,
      });
      onAdded && onAdded();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = "Création impossible.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic 422 validation errors
        msg = detail.map((d) => `${(d.loc || []).slice(-1)[0] || "champ"} : ${d.msg}`).join(" · ");
      } else if (e?.message) {
        msg = `Création impossible : ${e.message}`;
      }
      onError && onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-[rgba(110,231,197,0.30)] bg-[rgba(110,231,197,0.04)] p-3 grid sm:grid-cols-2 gap-2 mt-1"
         data-testid="manage-add-user-form">
      <div className="sm:col-span-2 grid grid-cols-[100px_1fr] gap-2 items-end">
        <select className="mc-input text-[12px]" value={form.id_type} data-testid="add-user-id-type"
                onChange={onChange("id_type")}>
          <option>RPPS</option><option>ADELI</option>
        </select>
        <div className="grid gap-1">
          <input className="mc-input text-[12px]" placeholder="N° pro (9 ou 11 chiffres) *"
                 data-testid="add-user-professional-id"
                 value={form.professional_id}
                 onChange={onChange("professional_id")} />
          {lookupState.status !== "idle" && (
            <div className={`text-[10.5px] inline-flex items-center gap-1 ${
              lookupState.status === "found" ? "text-[#047857]"
              : lookupState.status === "loading" ? "text-[var(--mc-muted)]"
              : "text-[#DC2626]"
            }`} data-testid="add-user-lookup-status">
              {lookupState.status === "found" && <Check size={10} />}
              {lookupState.status === "loading" && <RefreshCw size={10} className="animate-spin" />}
              {lookupState.status === "not_found" && <AlertTriangle size={10} />}
              {lookupState.message}
            </div>
          )}
        </div>
      </div>
      <input className="mc-input text-[12px]" placeholder="Prénom *" data-testid="add-user-first-name"
             value={form.first_name} onChange={onChange("first_name")} />
      <input className="mc-input text-[12px]" placeholder="Nom *" data-testid="add-user-last-name"
             value={form.last_name} onChange={onChange("last_name")} />
      <input className="mc-input text-[12px]" placeholder="Email *" data-testid="add-user-email"
             value={form.email} onChange={onChange("email")} />
      <input className="mc-input text-[12px]" placeholder="Téléphone" data-testid="add-user-phone"
             value={form.phone} onChange={onChange("phone")} />
      <input className="mc-input text-[12px] sm:col-span-2" placeholder="Profession *" data-testid="add-user-profession"
             value={form.profession} onChange={onChange("profession")} />
      <div className="sm:col-span-2 flex justify-end">
        <button onClick={submit} disabled={saving || !form.first_name || !form.last_name || !form.email || !form.professional_id}
                data-testid="add-user-submit"
                className="btn-primary px-4 py-1.5 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
          <Plus size={11} /> {saving ? "Ajout…" : "Ajouter l'utilisateur"}
        </button>
      </div>
    </div>
  );
}

function BalAccessEditor({ mailbox, users, onSave, saving }) {
  const [coIds, setCoIds] = useState(mailbox.cotitulaire_user_ids || []);
  const [deIds, setDeIds] = useState(mailbox.delegataire_user_ids || []);
  const respId = mailbox.responsible_user_id;
  const eligible = users.filter((u) => u.id && u.id !== respId);
  const initialKey = useMemo(
    () => JSON.stringify([mailbox.cotitulaire_user_ids || [], mailbox.delegataire_user_ids || []]),
    [mailbox.cotitulaire_user_ids, mailbox.delegataire_user_ids]
  );
  const dirty = useMemo(
    () => JSON.stringify([coIds, deIds]) !== initialKey,
    [coIds, deIds, initialKey]
  );

  const setRole = (uid, role) => {
    setCoIds((prev) => role === "cotitulaire" ? Array.from(new Set([...prev, uid])) : prev.filter((x) => x !== uid));
    setDeIds((prev) => role === "delegataire" ? Array.from(new Set([...prev, uid])) : prev.filter((x) => x !== uid));
  };

  return (
    <div className="rounded-lg border border-[rgba(127,179,255,0.16)] bg-[rgba(45,123,255,0.04)] p-3"
         data-testid={`bal-access-${mailbox.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-[13px] text-[#0F2B5B] font-mono truncate">{mailbox.full_email}</div>
        <div className="text-[11px] text-[var(--mc-muted)]">Responsable : {mailbox.responsible}</div>
      </div>
      <div className="grid gap-1">
        {eligible.length === 0 && (
          <div className="text-[12px] italic text-[var(--mc-muted)]">Aucun utilisateur éligible pour cette BAL.</div>
        )}
        {eligible.map((u) => {
          const role = coIds.includes(u.id) ? "cotitulaire" : deIds.includes(u.id) ? "delegataire" : "none";
          return (
            <div key={u.id} className="flex items-center justify-between gap-2 py-1">
              <div className="text-[12.5px] text-[#334155] truncate">
                {u.first_name} {u.last_name} <span className="text-[var(--mc-muted)] text-[11px]">— {u.profession}</span>
              </div>
              <div className="inline-flex rounded-full border border-[rgba(127,179,255,0.20)] p-0.5">
                {["none", "cotitulaire", "delegataire"].map((r) => (
                  <button key={r}
                          onClick={() => setRole(u.id, r)}
                          data-testid={`bal-${mailbox.id}-user-${u.id}-${r}`}
                          className={`px-2.5 py-0.5 text-[10.5px] rounded-full transition ${role === r ? (r === "none" ? "bg-[#E2E8F0] text-[#0F2B5B]" : r === "cotitulaire" ? "bg-[rgba(110,231,197,0.18)] text-[#047857]" : "bg-[rgba(168,140,255,0.20)] text-[#7C3AED]") : "text-[var(--mc-muted)]"}`}>
                    {r === "none" ? "Aucun" : r === "cotitulaire" ? "Cotitulaire" : "Délégataire"}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {dirty && (
        <div className="flex justify-end mt-2">
          <button onClick={() => onSave(coIds, deIds)} disabled={saving}
                  data-testid={`bal-${mailbox.id}-save`}
                  className="btn-primary px-3 py-1 rounded-full text-[11.5px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
            <Save size={11} /> {saving ? "Enregistrement…" : "Enregistrer les accès"}
          </button>
        </div>
      )}
    </div>
  );
}


function SendConfirmationEmailModal({ orderId, onClose, onSent }) {
  const { user: admin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [selected, setSelected] = useState({});
  const [ccText, setCcText] = useState("");
  const [attachContract, setAttachContract] = useState(true);
  const [mode, setMode] = useState("preview"); // "preview" | "edit"
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/mssante-orders/${orderId}/email-draft`);
        if (!alive) return;
        setDraft(data);
        setSubject(data.subject || "");
        setBodyHtml(data.body_html || "");
        setCcText((data.default_cc || []).join(", "));
        // Pre-select all recipients by default
        const sel = {};
        (data.recipients || []).forEach((r) => { sel[r.email] = true; });
        setSelected(sel);
      } catch (e) {
        setError(e?.response?.data?.detail || "Impossible de charger le brouillon.");
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId]);

  const selectedEmails = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([e]) => e),
    [selected]
  );
  const ccList = useMemo(
    () => ccText.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@")),
    [ccText]
  );

  const openPdfPreview = () => {
    // Open the backend PDF endpoint directly in a new tab. We pass the admin
    // JWT via `?t=` so the browser doesn't need to send the Authorization
    // header. URL stays clean and bookmarkable for the session.
    const token = localStorage.getItem("mc_token") || "";
    const url = `${API}/admin/mssante-orders/${orderId}/contract.pdf?t=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const sendTestToSelf = async () => {
    if (!admin?.email) {
      setError("Impossible de récupérer votre email admin.");
      return;
    }
    setTesting(true);
    setError("");
    setInfo("");
    try {
      await axios.post(
        `${API}/admin/mssante-orders/${orderId}/send-confirmation-email`,
        {
          subject: `[TEST] ${subject}`,
          body_html: bodyHtml,
          recipients: [admin.email],
          cc: [],
          attach_contract: attachContract,
        }
      );
      setInfo(`Email de test envoyé à ${admin.email}${attachContract ? " (PDF du contrat attaché)" : ""}. Aucun envoi côté client.`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Test impossible.");
    } finally {
      setTesting(false);
    }
  };

  const send = async () => {
    if (selectedEmails.length === 0) {
      setError("Sélectionnez au moins un destinataire.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/mssante-orders/${orderId}/send-confirmation-email`,
        {
          subject,
          body_html: bodyHtml,
          recipients: selectedEmails,
          cc: ccList,
          attach_contract: attachContract,
        }
      );
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid={`email-modal-${orderId}`}
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl border border-[rgba(127,179,255,0.30)] bg-[#0A1A33] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[rgba(127,179,255,0.18)]">
          <div className="flex items-center gap-2 min-w-0">
            <Mail size={16} className="text-[#047857] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#0284C7]">
                Email de confirmation MSSanté
              </div>
              <div className="text-[13.5px] text-[#0F2B5B] truncate">
                {draft?.mailbox_full_email || "…"}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="email-modal-close"
            className="text-[var(--mc-muted)] hover:text-[#0F2B5B] p-1.5 rounded-md hover:bg-[#F1F5F9]"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 grid gap-4">
          {loading && <div className="text-[13px] text-[var(--mc-muted)]">Chargement…</div>}
          {!loading && draft && !result && (
            <>
              {/* Recipients */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#0284C7] mb-2">
                  Destinataires ({selectedEmails.length}/{(draft.recipients || []).length})
                </div>
                <div className="grid gap-1.5">
                  {(draft.recipients || []).map((r) => (
                    <label
                      key={r.email}
                      className="flex items-center gap-3 p-2.5 rounded-lg border border-[rgba(127,179,255,0.16)] bg-[rgba(45,123,255,0.04)] hover:border-[rgba(127,179,255,0.32)] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[r.email]}
                        onChange={(e) => setSelected((s) => ({ ...s, [r.email]: e.target.checked }))}
                        data-testid={`email-recipient-${r.email}`}
                        className="h-4 w-4 accent-[var(--mc-mint)]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-[#0F2B5B] truncate">
                          {r.name} <span className="text-[var(--mc-muted)] text-[11.5px]">— {r.role}</span>
                        </div>
                        <div className="text-[11.5px] text-[var(--mc-muted)] truncate font-mono">{r.email}</div>
                      </div>
                    </label>
                  ))}
                  {(draft.recipients || []).length === 0 && (
                    <div className="text-[12.5px] text-[var(--mc-muted)] italic">
                      Aucun destinataire trouvé pour cette commande.
                    </div>
                  )}
                </div>
              </div>

              {/* CC + attachment options */}
              <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.22em] text-[#0284C7] mb-1.5">
                    Copie (Cc) — séparer par virgule
                  </label>
                  <input
                    type="text"
                    value={ccText}
                    onChange={(e) => setCcText(e.target.value)}
                    placeholder="contact@ohmyvue.fr"
                    data-testid="email-cc"
                    className="mc-input w-full font-mono text-[12.5px]"
                  />
                </div>
                <label className="inline-flex items-center gap-2 text-[12.5px] text-[#334155] cursor-pointer select-none pb-2">
                  <input
                    type="checkbox"
                    checked={attachContract}
                    onChange={(e) => setAttachContract(e.target.checked)}
                    data-testid="email-attach-contract"
                    className="h-4 w-4 accent-[var(--mc-mint)]"
                  />
                  <span>
                    Joindre le contrat signé (PDF)
                    {draft?.contract_accepted_at && (
                      <span className="block text-[10.5px] text-[var(--mc-muted)]">
                        Accepté le {new Date(draft.contract_accepted_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    )}
                  </span>
                </label>
              </div>

              {/* Preview / test actions */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={openPdfPreview}
                  data-testid="email-preview-pdf"
                  className="px-3 py-1.5 rounded-full text-[11.5px] font-medium inline-flex items-center gap-1.5 border border-[rgba(127,179,255,0.30)] text-[#0284C7] hover:bg-[rgba(45,123,255,0.10)]"
                  title="Ouvre le PDF du contrat signé dans un nouvel onglet"
                >
                  <FileText size={12} />
                  Prévisualiser le contrat (PDF)
                </button>
                <button
                  type="button"
                  onClick={sendTestToSelf}
                  disabled={testing}
                  data-testid="email-test-self"
                  className="px-3 py-1.5 rounded-full text-[11.5px] font-medium inline-flex items-center gap-1.5 border border-[rgba(168,140,255,0.40)] text-[#7C3AED] hover:bg-[rgba(168,140,255,0.10)] disabled:opacity-50"
                  title={admin?.email ? `Envoyer un email de test à ${admin.email}` : "Connecté admin requis"}
                >
                  <MailCheck size={12} />
                  {testing ? "Envoi du test…" : "Envoyer un test à moi-même"}
                </button>
                {info && (
                  <span className="text-[11px] text-[#047857] inline-flex items-center gap-1">
                    <Check size={11} /> {info}
                  </span>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#0284C7] mb-1.5">
                  Sujet
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="email-subject"
                  className="mc-input w-full"
                />
              </div>

              {/* Body — toggle Aperçu / Édition */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#0284C7]">
                    Corps du message
                  </label>
                  <div className="inline-flex rounded-full border border-[rgba(127,179,255,0.20)] p-0.5">
                    <button
                      onClick={() => setMode("preview")}
                      data-testid="email-mode-preview"
                      className={`px-3 py-1 text-[11px] rounded-full inline-flex items-center gap-1.5 ${mode === "preview" ? "bg-[rgba(110,231,197,0.18)] text-[#047857]" : "text-[var(--mc-muted)]"}`}
                    >
                      <Eye size={11} /> Aperçu
                    </button>
                    <button
                      onClick={() => setMode("edit")}
                      data-testid="email-mode-edit"
                      className={`px-3 py-1 text-[11px] rounded-full inline-flex items-center gap-1.5 ${mode === "edit" ? "bg-[rgba(127,179,255,0.20)] text-[#0F2B5B]" : "text-[var(--mc-muted)]"}`}
                    >
                      <Edit3 size={11} /> Édition HTML
                    </button>
                  </div>
                </div>
                {mode === "preview" ? (
                  <div
                    className="rounded-lg border border-[rgba(127,179,255,0.18)] bg-white text-[#0F1A35] p-4 max-h-[40vh] overflow-y-auto"
                    data-testid="email-preview"
                    dangerouslySetInnerHTML={{ __html: bodyHtml }}
                  />
                ) : (
                  <textarea
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                    data-testid="email-body-html"
                    rows={14}
                    className="mc-input w-full font-mono text-[12px] resize-y"
                  />
                )}
              </div>

              {(draft.email_history || []).length > 0 && (
                <div className="rounded-lg border border-[rgba(127,179,255,0.18)] bg-[rgba(45,123,255,0.04)] p-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#0284C7] mb-1.5">
                    Historique d&apos;envoi ({draft.email_history.length})
                  </div>
                  <ul className="text-[11.5px] text-[var(--mc-muted)] grid gap-1">
                    {draft.email_history.slice(-5).reverse().map((h, i) => (
                      <li key={i}>
                        <span className="text-[#334155]">
                          {new Date(h.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                        {" → "}
                        <span className="font-mono">{(h.recipients_sent || []).join(", ")}</span>
                        {h.recipients_failed?.length > 0 && (
                          <span className="text-[#DC2626]"> · échec: {h.recipients_failed.join(", ")}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="rounded-lg border border-[rgba(110,231,197,0.45)] bg-[rgba(110,231,197,0.08)] p-4 grid gap-2">
              <div className="text-[#047857] text-[14px] inline-flex items-center gap-2">
                <Check size={14} /> Email envoyé avec succès
              </div>
              <div className="text-[12.5px] text-[#334155]">
                Envoyé à : <span className="font-mono">{result.sent_to.join(", ")}</span>
              </div>
              {result.failed?.length > 0 && (
                <div className="text-[12.5px] text-[#DC2626]">
                  Échecs : <span className="font-mono">{result.failed.map((f) => f.email).join(", ")}</span>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.06)] p-3 text-[12.5px] text-[#DC2626]">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[rgba(127,179,255,0.18)]">
          <button
            onClick={onClose}
            className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium"
          >
            {result ? "Fermer" : "Annuler"}
          </button>
          {!result && (
            <button
              onClick={send}
              disabled={sending || loading || selectedEmails.length === 0}
              data-testid="email-send-button"
              className="btn-primary px-5 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Send size={12} />
              {sending ? "Envoi en cours…" : `Envoyer (${selectedEmails.length})`}
            </button>
          )}
          {result && (
            <button
              onClick={onSent}
              className="btn-primary px-5 py-2 rounded-full text-[12px] font-medium"
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MailboxRow({ m, onChanged }) {
  const [workflow, setWorkflow] = useState(m.workflow_status || "received");
  const [adminNote, setAdminNote] = useState(m.admin_note || "");
  const [maincareDate, setMaincareDate] = useState(
    m.maincare_created_at ? m.maincare_created_at.slice(0, 10) : ""
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const dirty =
    workflow !== (m.workflow_status || "received") ||
    (adminNote || "") !== (m.admin_note || "") ||
    maincareDate !== (m.maincare_created_at ? m.maincare_created_at.slice(0, 10) : "");

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/mssante-orders/${m.id}`, {
        workflow_status: workflow,
        admin_note: adminNote,
        maincare_created_at: maincareDate || "",
      });
      setSavedAt(Date.now());
      onChanged && onChanged();
    } catch (e) {
      alert("Mise à jour impossible.");
    } finally {
      setSaving(false);
    }
  };

  const acknowledge = async () => {
    try {
      await axios.post(`${API}/admin/mssante-orders/${m.id}/acknowledge`);
      onChanged && onChanged();
    } catch (e) {
      alert("Impossible d'acquitter les modifications.");
    }
  };

  const log = (m.pending_changes_log || []).slice(-3).reverse();

  return (
    <div
      className="rounded-lg border border-[rgba(127,179,255,0.14)] bg-[rgba(45,123,255,0.04)] p-3 lg:p-4 grid gap-3"
      data-testid={`mailbox-${m.id}`}
    >
      {/* Header: email + workflow badge + payment badge */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Mail size={14} className="text-[#047857] shrink-0" />
          <CopyChip value={m.full_email} label="email" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${
              WORKFLOW_COLOR[m.workflow_status] || WORKFLOW_COLOR.received
            }`}
            data-testid={`mailbox-${m.id}-workflow-badge`}
          >
            {WORKFLOW_LABEL[m.workflow_status] || "Reçue"}
          </span>
          <span
            className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${
              PAYMENT_COLOR[m.payment_status] || PAYMENT_COLOR.pending_payment
            }`}
            title="Statut de paiement Stripe (lecture seule)"
            data-testid={`mailbox-${m.id}-payment-badge`}
          >
            {PAYMENT_LABEL[m.payment_status] || m.payment_status}
            {m.billing_plan ? ` · ${m.billing_plan === "yearly" ? "Annuel" : "Mensuel"}` : ""}
          </span>
        </div>
      </div>

      {/* Pending changes notification banner */}
      {m.has_pending_admin_changes && (
        <div
          className="flex items-start gap-3 rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.06)] p-3"
          data-testid={`mailbox-${m.id}-pending-changes`}
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[#DC2626]" />
          <div className="flex-1 min-w-0 grid gap-1">
            <div className="text-[12.5px] text-[#DC2626] font-medium">
              Modifications récentes par le professionnel
            </div>
            {log.length > 0 && (
              <ul className="text-[11.5px] text-[var(--mc-muted)] grid gap-0.5">
                {log.map((entry) => (
                  <li key={`${entry.at}-${entry.kind}`}>
                    <span className="text-[#334155]">{entry.description}</span>
                    <span className="text-[var(--mc-muted)] ml-1">
                      · {new Date(entry.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            onClick={acknowledge}
            data-testid={`mailbox-${m.id}-acknowledge`}
            className="text-[11px] px-3 py-1 rounded-full border border-[rgba(255,180,180,0.35)] text-[#DC2626] hover:bg-[rgba(255,120,120,0.12)] shrink-0"
          >
            Acquitter
          </button>
        </div>
      )}

      {/* Metadata */}
      <div className="text-[12px] text-[var(--mc-muted)] grid sm:grid-cols-2 gap-x-4 gap-y-1">
        <span>Responsable : <span className="text-[#0F2B5B]">{m.responsible || "-"}</span></span>
        <span>Liste rouge : <span className="text-[#0F2B5B]">{m.red_list ? "Oui" : "Non"}</span></span>
        {m.billing_ref && <span>Réf. fact. : <span className="text-[#0F2B5B]">{m.billing_ref}</span></span>}
        {m.created_at && <span>Commandée : <span className="text-[#0F2B5B]">{new Date(m.created_at).toLocaleDateString("fr-FR")}</span></span>}
      </div>
      {m.description && <p className="text-[12.5px] text-[#475569] leading-relaxed">{m.description}</p>}

      {/* Admin workflow edit block */}
      <div className="rounded-lg border border-[rgba(127,179,255,0.18)] bg-[rgba(45,123,255,0.06)] p-3 grid gap-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#0284C7]">
          Gestion admin
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] text-[#0284C7] mb-1.5">
              Statut workflow
            </label>
            <select
              value={workflow}
              onChange={(e) => setWorkflow(e.target.value)}
              data-testid={`mailbox-${m.id}-workflow-select`}
              className="mc-input w-full"
            >
              {WORKFLOW_OPTIONS.map((w) => (
                <option key={w} value={w}>
                  {WORKFLOW_LABEL[w]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] text-[#0284C7] mb-1.5">
              Date création Maincare
            </label>
            <input
              type="date"
              value={maincareDate}
              onChange={(e) => setMaincareDate(e.target.value)}
              data-testid={`mailbox-${m.id}-maincare-date`}
              className="mc-input w-full"
            />
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.16em] text-[#0284C7] mb-1.5">
            Note admin (interne)
          </label>
          <textarea
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            data-testid={`mailbox-${m.id}-admin-note`}
            rows={2}
            maxLength={4000}
            placeholder="Tracer : date de création, contact Maincare, incidents, etc."
            className="mc-input w-full resize-y"
          />
        </div>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {savedAt && !dirty && (
            <span className="text-[11px] text-[#047857] inline-flex items-center gap-1">
              <Check size={11} /> Enregistré
            </span>
          )}
          <button
            onClick={() => setShowEmailModal(true)}
            data-testid={`mailbox-${m.id}-send-email`}
            className="px-4 py-1.5 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 border border-[rgba(110,231,197,0.45)] text-[#047857] hover:bg-[rgba(110,231,197,0.10)]"
            title="Envoyer l'email de confirmation MSSanté au client"
          >
            <Mail size={12} /> Envoyer email
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid={`mailbox-${m.id}-save`}
            className="btn-primary px-4 py-1.5 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
      {showEmailModal && (
        <SendConfirmationEmailModal
          orderId={m.id}
          onClose={() => setShowEmailModal(false)}
          onSent={() => { setShowEmailModal(false); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

function UserRow({ u }) {
  const idMeta = getIdTypeMeta(u.id_type);
  const badge = getUserBadge(u.id_type);
  const respCount = (u.is_responsible_of || []).length;
  const cotCount = (u.as_cotitulaire || []).length;
  const delCount = (u.as_delegataire || []).length;
  return (
    <div className="rounded-lg border border-[rgba(127,179,255,0.14)] bg-[rgba(45,123,255,0.04)] p-3 lg:p-4 grid sm:grid-cols-[1fr_auto] items-start gap-3"
         data-testid={`user-${u.professional_id || u.email}`}>
      <div className="min-w-0 grid gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[#0F2B5B] font-medium">{u.first_name} {u.last_name}</span>
          {badge && (
            <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${
              badge.kind === "soignant"
                ? "border-[rgba(110,231,197,0.45)] text-[#047857] bg-[rgba(110,231,197,0.06)]"
                : "border-[rgba(127,179,255,0.32)] text-[#0284C7] bg-[rgba(45,123,255,0.06)]"
            }`}>{badge.label}</span>
          )}
          {respCount > 0 && (
            <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border border-[rgba(255,170,80,0.45)] text-[#ffce99] bg-[rgba(255,170,80,0.06)]">
              Resp. de {respCount}
            </span>
          )}
          {cotCount > 0 && (
            <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border border-[rgba(127,179,255,0.32)] text-[#0284C7] bg-[rgba(45,123,255,0.06)]">
              Cotit. {cotCount}
            </span>
          )}
          {delCount > 0 && (
            <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border border-[rgba(168,140,255,0.4)] text-[#7C3AED] bg-[rgba(168,140,255,0.06)]">
              Délég. {delCount}
            </span>
          )}
        </div>
        <div className="text-[12px] text-[var(--mc-muted)]">
          {u.profession}
          {idMeta && u.professional_id && <> · {idMeta.label} <span className="font-mono text-[#475569]">{u.professional_id}</span></>}
        </div>
      </div>
      <div className="grid gap-1 justify-start sm:justify-end">
        <CopyChip value={u.email} label="email" />
        {u.phone && <CopyChip value={u.phone} label="téléphone" />}
        {(u.is_responsible_of || []).map((m) => (
          <span key={`r-${m}`} className="text-[11px] text-[#047857] inline-flex items-center gap-1">
            <Mail size={10} /> {m} <span className="opacity-60">(resp.)</span>
          </span>
        ))}
        {(u.as_cotitulaire || []).map((m) => (
          <span key={`c-${m}`} className="text-[11px] text-[#0284C7] inline-flex items-center gap-1">
            <Mail size={10} /> {m} <span className="opacity-60">(cotit.)</span>
          </span>
        ))}
        {(u.as_delegataire || []).map((m) => (
          <span key={`d-${m}`} className="text-[11px] text-[#7C3AED] inline-flex items-center gap-1">
            <Mail size={10} /> {m} <span className="opacity-60">(délég.)</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function UsersTable({ items }) {
  if (items.length === 0) return <Empty icon={Users} text="Aucun utilisateur." />;
  return (
    <div className="mc-card overflow-hidden" data-testid="admin-mss-users">
      <table className="w-full text-[13px]">
        <thead className="bg-[rgba(45,123,255,0.06)] text-[10px] uppercase tracking-[0.22em] text-[#0284C7]">
          <tr>
            <th className="text-left px-4 py-3">Identifiant</th>
            <th className="text-left px-4 py-3">Nom / Prénom</th>
            <th className="text-left px-4 py-3">Profession</th>
            <th className="text-left px-4 py-3">Coordonnées</th>
            <th className="text-left px-4 py-3">Structure</th>
            <th className="text-left px-4 py-3">Type</th>
          </tr>
        </thead>
        <tbody>
          {items.map((u) => {
            const meta = getIdTypeMeta(u.id_type);
            const badge = getUserBadge(u.id_type);
            const rowKey = `${u.structure_id || ""}:${u.id || u.professional_id || u.email}`;
            return (
              <tr key={rowKey} className="border-t border-[rgba(127,179,255,0.10)] hover:bg-[rgba(45,123,255,0.04)]">
                <td className="px-4 py-3 font-mono text-[#334155] text-[12px]">{meta?.label || u.id_type || "-"} {u.professional_id || "-"}</td>
                <td className="px-4 py-3 text-[#0F2B5B]">{u.last_name} {u.first_name}</td>
                <td className="px-4 py-3 text-[var(--mc-muted)]">{u.profession}</td>
                <td className="px-4 py-3 text-[12px] text-[var(--mc-muted)]">
                  <div className="text-[#334155]">{u.email}</div>
                  {u.phone && <div>{u.phone}</div>}
                </td>
                <td className="px-4 py-3 text-[12px]">
                  <div className="text-[#334155] truncate max-w-[180px]">{u.structure_name}</div>
                  <div className="text-[var(--mc-muted)] font-mono">{u.structure_id_type} {u.structure_id}</div>
                </td>
                <td className="px-4 py-3">
                  {badge && (
                    <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${
                      badge.kind === "soignant"
                        ? "border-[rgba(110,231,197,0.45)] text-[#047857] bg-[rgba(110,231,197,0.06)]"
                        : "border-[rgba(127,179,255,0.32)] text-[#0284C7] bg-[rgba(45,123,255,0.06)]"
                    }`}>{badge.label}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MailboxesTable({ items, onChanged }) {
  if (items.length === 0) return <Empty icon={Mail} text="Aucune boîte aux lettres." />;
  return (
    <div className="mc-card overflow-hidden" data-testid="admin-mss-mailboxes">
      <table className="w-full text-[13px]">
        <thead className="bg-[rgba(45,123,255,0.06)] text-[10px] uppercase tracking-[0.22em] text-[#0284C7]">
          <tr>
            <th className="text-left px-4 py-3">Workflow</th>
            <th className="text-left px-4 py-3">Paiement</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Structure</th>
            <th className="text-left px-4 py-3">Responsable</th>
            <th className="text-left px-4 py-3">Commandée</th>
            <th className="text-left px-4 py-3">Maincare</th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className="border-t border-[rgba(127,179,255,0.10)] hover:bg-[rgba(45,123,255,0.04)]">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${WORKFLOW_COLOR[m.workflow_status] || WORKFLOW_COLOR.received}`}>
                    {WORKFLOW_LABEL[m.workflow_status] || "Reçue"}
                  </span>
                  {m.has_pending_admin_changes && (
                    <span title="Modifications en attente d'acquittement" data-testid={`mailbox-table-${m.id}-badge-pending`}>
                      <AlertTriangle size={13} className="text-[#DC2626]" />
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${PAYMENT_COLOR[m.payment_status] || PAYMENT_COLOR.pending_payment}`}>
                  {PAYMENT_LABEL[m.payment_status] || m.payment_status}
                </span>
              </td>
              <td className="px-4 py-3 font-mono text-[#334155] text-[12px]">{m.full_email}</td>
              <td className="px-4 py-3 text-[12px]">
                <div className="text-[#334155] truncate max-w-[180px]">{m.structure_name}</div>
                <div className="text-[var(--mc-muted)] font-mono">{m.structure_id_type} {m.structure_id}</div>
              </td>
              <td className="px-4 py-3 text-[#0F2B5B]">{m.responsible || "-"}</td>
              <td className="px-4 py-3 text-[var(--mc-muted)]">
                {m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : "-"}
              </td>
              <td className="px-4 py-3 text-[var(--mc-muted)]">
                {m.maincare_created_at ? new Date(m.maincare_created_at).toLocaleDateString("fr-FR") : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="mc-card p-12 text-center">
      <div className="mx-auto grid place-items-center h-12 w-12 rounded-full bg-[rgba(127,179,255,0.10)] border border-[rgba(127,179,255,0.18)] text-[var(--mc-muted)] mb-3">
        <Icon size={18} />
      </div>
      <p className="text-[var(--mc-muted)] text-[13px]">{text}</p>
    </div>
  );
}


function ProAccountsTable({ items, query, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [showPurge, setShowPurge] = useState(false);
  const [purgeText, setPurgeText] = useState("");
  const [feedback, setFeedback] = useState("");

  const filtered = useMemo(() => {
    if (!query?.trim()) return items;
    const q = query.toLowerCase().trim();
    return items.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.first_name || "").toLowerCase().includes(q) ||
      (u.last_name || "").toLowerCase().includes(q) ||
      (u.profession || "").toLowerCase().includes(q) ||
      (u.professional_id || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const deleteOne = async (u) => {
    setBusy(true);
    setFeedback("");
    try {
      const { data } = await axios.delete(`${API}/admin/pro-users/${u.id}`);
      setFeedback(`✓ Compte ${data.email} supprimé (cascade : ${Object.entries(data.deleted).filter(([,v])=>v>0).map(([k,v])=>`${k}=${v}`).join(", ") || "0"})`);
      setConfirmId(null);
      await onChanged();
    } catch (e) {
      setFeedback(`✗ ${e?.response?.data?.detail || "Suppression impossible."}`);
    } finally { setBusy(false); }
  };

  const purgeAll = async () => {
    setBusy(true);
    setFeedback("");
    try {
      const { data } = await axios.post(`${API}/admin/pro-users/purge-all`, { confirm: "PURGE_ALL_PRO" });
      setFeedback(`✓ Purge totale effectuée : ${Object.entries(data.deleted).filter(([,v])=>v>0).map(([k,v])=>`${k}=${v}`).join(", ") || "rien à purger"}`);
      setShowPurge(false);
      setPurgeText("");
      await onChanged();
    } catch (e) {
      setFeedback(`✗ ${e?.response?.data?.detail || "Purge impossible."}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-4">
      <div className="rounded-xl border border-[rgba(255,120,120,0.30)] bg-[rgba(255,120,120,0.05)] p-4 lg:p-5 flex items-start gap-3 flex-wrap">
        <AlertTriangle size={18} className="text-[#DC2626] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-[240px]">
          <div className="text-[#0F2B5B] font-medium text-[14px] mb-1">Zone d'administration destructive</div>
          <p className="text-[12.5px] text-[var(--mc-muted)] leading-relaxed">
            La suppression d'un compte pro entraîne la suppression en cascade de
            <strong className="text-[#0F2B5B]"> ses structures, utilisateurs liés, boîtes mail commandées, codes OTP et secrets 2FA</strong>. Action irréversible.
          </p>
        </div>
        {!showPurge ? (
          <button onClick={() => setShowPurge(true)} data-testid="admin-purge-open"
                  className="px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 bg-[rgba(255,120,120,0.18)] border border-[rgba(255,120,120,0.4)] text-[#DC2626] hover:bg-[rgba(255,120,120,0.28)]">
            <Trash2 size={12} /> Purger TOUS les comptes
          </button>
        ) : (
          <div className="w-full mt-3 grid gap-2 md:flex md:items-center md:gap-2">
            <span className="text-[12px] text-[#DC2626]">Pour confirmer, tapez exactement :</span>
            <code className="text-[12px] font-mono text-white bg-[rgba(13,22,46,0.6)] border border-[rgba(255,120,120,0.30)] px-2 py-1 rounded">PURGE_ALL_PRO</code>
            <input value={purgeText} onChange={(e) => setPurgeText(e.target.value)}
                   data-testid="admin-purge-confirm-input"
                   className="mc-input flex-1 font-mono" placeholder="PURGE_ALL_PRO" />
            <div className="flex items-center gap-2">
              <button onClick={() => { setShowPurge(false); setPurgeText(""); }}
                      className="btn-ghost px-3 py-2 rounded-full text-[12px]">Annuler</button>
              <button onClick={purgeAll} disabled={busy || purgeText !== "PURGE_ALL_PRO"}
                      data-testid="admin-purge-submit"
                      className="px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 bg-[#ff6b6b] text-white hover:bg-[#ff5252] disabled:opacity-40">
                <Trash2 size={12} /> Confirmer la purge
              </button>
            </div>
          </div>
        )}
      </div>

      {feedback && (
        <div className={`rounded-lg border px-4 py-2.5 text-[12.5px] ${
          feedback.startsWith("✓")
            ? "border-[rgba(110,231,197,0.4)] bg-[rgba(110,231,197,0.06)] text-[#047857]"
            : "border-[rgba(255,120,120,0.4)] bg-[rgba(255,120,120,0.06)] text-[#DC2626]"
        }`} data-testid="admin-pro-feedback">
          {feedback}
        </div>
      )}

      {filtered.length === 0 ? (
        <Empty icon={Users} text="Aucun compte pro." />
      ) : (
        <div className="mc-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-[rgba(127,179,255,0.06)] text-[10.5px] uppercase tracking-[0.18em] text-[var(--mc-muted)]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Pro</th>
                  <th className="text-left px-4 py-2.5 font-medium">Identifiant</th>
                  <th className="text-center px-4 py-2.5 font-medium">2FA</th>
                  <th className="text-center px-4 py-2.5 font-medium">Vérifié</th>
                  <th className="text-center px-4 py-2.5 font-medium">Structures</th>
                  <th className="text-center px-4 py-2.5 font-medium">Users</th>
                  <th className="text-center px-4 py-2.5 font-medium">BAL</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(127,179,255,0.08)]">
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`admin-pro-row-${u.id}`} className="hover:bg-[rgba(127,179,255,0.04)]">
                    <td className="px-4 py-3">
                      <div className="text-[#0F2B5B] font-medium">{u.first_name} {u.last_name}</div>
                      <div className="text-[11.5px] text-[var(--mc-muted)] mt-0.5">{u.email}</div>
                      <div className="text-[10.5px] text-[var(--mc-muted)] mt-0.5">{u.profession}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11.5px] text-[#0284C7]">
                      {u.id_type ? `${u.id_type} ${u.professional_id || ""}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.twofa_enabled ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[rgba(110,231,197,0.4)] text-[#047857]">
                          <KeyRound size={10} /> ON
                        </span>
                      ) : <span className="text-[var(--mc-muted)] text-[11px]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.email_verified
                        ? <Check size={14} className="text-[#047857] mx-auto" />
                        : <span className="text-[#B45309] text-[11px]">non</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-[#0F2B5B]">{u.structure_count || 0}</td>
                    <td className="px-4 py-3 text-center text-[#0F2B5B]">{u.structure_user_count || 0}</td>
                    <td className="px-4 py-3 text-center text-[#0F2B5B]">{u.mailbox_count || 0}</td>
                    <td className="px-4 py-3 text-right">
                      {confirmId === u.id ? (
                        <div className="inline-flex items-center gap-1.5">
                          <button onClick={() => setConfirmId(null)}
                                  className="px-2.5 py-1 rounded-full text-[11px] border border-[rgba(127,179,255,0.22)] text-[var(--mc-muted)] hover:text-[#0F2B5B]">
                            Annuler
                          </button>
                          <button onClick={() => deleteOne(u)} disabled={busy}
                                  data-testid={`admin-pro-delete-confirm-${u.id}`}
                                  className="px-2.5 py-1 rounded-full text-[11px] inline-flex items-center gap-1 bg-[#ff6b6b] text-white hover:bg-[#ff5252] disabled:opacity-50">
                            <Trash2 size={10} /> Confirmer
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setConfirmId(u.id); setFeedback(""); }}
                                data-testid={`admin-pro-delete-${u.id}`}
                                className="px-2.5 py-1 rounded-full text-[11px] inline-flex items-center gap-1 border border-[rgba(255,120,120,0.32)] text-[#DC2626] hover:bg-[rgba(255,120,120,0.10)]">
                          <Trash2 size={10} /> Supprimer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
